import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Dashboard.css';
import AccountStatusBanner from './AccountStatusBanner';
import BidEditModal from './BidEditModal';
import Messages from './Messages';
import NotificationBell from './NotificationBell';
import SubmissionsPanel from './SubmissionsPanel';
import SubmitContentModal from './SubmitContentModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

function InfluencerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [activeTab, setActiveTab] = useState('browse');
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    contentType: '',
    minBudget: '',
    maxBudget: '',
  });
  const [badgeCounts, setBadgeCounts] = useState({
    pendingBids: 0,
    acceptedBids: 0,
    unreadMessages: 0,
    availableJobs: 0,
  });
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [messageConversationId, setMessageConversationId] = useState(null);
  // Submit content modal: { bid, previous } (previous = submission being revised)
  const [submitTarget, setSubmitTarget] = useState(null);
  const [submissionsReload, setSubmissionsReload] = useState(0);

  const openConversation = (conversationId) => {
    setActiveTab('messages');
    if (conversationId) setMessageConversationId(conversationId);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      navigate('/network');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.userType !== 'influencer') {
      navigate('/network/brand-dashboard');
      return;
    }

    setUser(userData);
    fetchJobs(token);
    fetchMyBids(token);
    fetchBadgeCounts(token);

    // Keep badge counts fresh (accepted bids, unread messages)
    const interval = setInterval(() => fetchBadgeCounts(token), 20000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchJobs = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyBids = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/bids/my-bids`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setMyBids(data.bids || []);
      }
    } catch (err) {
      console.error('Error fetching bids:', err);
    }
  };

  const fetchBadgeCounts = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/badge-counts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setBadgeCounts(data.counts || {});
      }
    } catch (err) {
      console.error('Error fetching badge counts:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/network');
  };

  const getContentTypeLabel = (type) => {
    const types = {
      feed: 'Feed Post',
      story: 'Story',
      reel: 'Reel',
      carousel: 'Carousel',
      mixed: 'Mixed Content',
    };
    return types[type] || type;
  };

  const filteredJobs = jobs.filter((job) => {
    if (filters.contentType && job.contentType !== filters.contentType) return false;
    if (filters.minBudget && job.budget < parseInt(filters.minBudget)) return false;
    if (filters.maxBudget && job.budget > parseInt(filters.maxBudget)) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src="/network-logo.png" alt="Alooma Network" className="sidebar-logo-img" />
          </Link>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            Browse Jobs
            {badgeCounts.availableJobs > 0 && (
              <span className="nav-badge">{badgeCounts.availableJobs}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'bids' ? 'active' : ''}`}
            onClick={() => setActiveTab('bids')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            My Bids
            {badgeCounts.pendingBids > 0 && (
              <span className="nav-badge">{badgeCounts.pendingBids}</span>
            )}
            {badgeCounts.acceptedBids > 0 && (
              <span className="nav-badge highlight">{badgeCounts.acceptedBids}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Messages
            {badgeCounts.unreadMessages > 0 && (
              <span className="nav-badge highlight">{badgeCounts.unreadMessages}</span>
            )}
          </button>
          <Link to="/network/influencer-profile" className="nav-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            My Profile
          </Link>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.fullName?.charAt(0) || 'C'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.fullName}</span>
              <span className="user-type">{user?.instagramHandle}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <h1>
              {activeTab === 'browse' && 'Browse Opportunities'}
              {activeTab === 'bids' && 'My Bids'}
              {activeTab === 'messages' && 'Messages'}
            </h1>
            <div className="header-actions">
              <NotificationBell onOpenConversation={openConversation} />
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          <AccountStatusBanner profileLink="/network/influencer-profile" />
          {activeTab === 'browse' && (
            <>
              {/* Filters */}
              <div className="filters-bar">
                <select
                  value={filters.contentType}
                  onChange={(e) => setFilters({ ...filters, contentType: e.target.value })}
                  className="filter-select"
                >
                  <option value="">All Content Types</option>
                  <option value="feed">Feed Post</option>
                  <option value="story">Story</option>
                  <option value="reel">Reel</option>
                  <option value="carousel">Carousel</option>
                  <option value="mixed">Mixed Content</option>
                </select>
                <input
                  type="number"
                  placeholder="Min Budget"
                  value={filters.minBudget}
                  onChange={(e) => setFilters({ ...filters, minBudget: e.target.value })}
                  className="filter-input"
                />
                <input
                  type="number"
                  placeholder="Max Budget"
                  value={filters.maxBudget}
                  onChange={(e) => setFilters({ ...filters, maxBudget: e.target.value })}
                  className="filter-input"
                />
              </div>

              <div className="jobs-grid">
                {filteredJobs.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>No opportunities available</h3>
                    <p>Check back later for new job postings from brands.</p>
                  </div>
                ) : (
                  filteredJobs.map((job) => (
                    <div key={job.id} className="job-card">
                      <div className="job-header">
                        <span className="job-brand">{job.brandName}</span>
                        <span className="job-date">
                          {new Date(job.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="job-title">{job.title}</h3>
                      <p className="job-description">{job.description?.substring(0, 120)}...</p>
                      <div className="job-tags">
                        <span className="tag">{getContentTypeLabel(job.contentType)}</span>
                        <span className="tag">{job.postCount} posts</span>
                      </div>
                      <div className="job-meta">
                        <div className="meta-item">
                          <span className="meta-label">Budget</span>
                          <span className="meta-value budget">${job.budget?.toLocaleString()}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Deadline</span>
                          <span className="meta-value">
                            {job.deadline ? new Date(job.deadline).toLocaleDateString() : 'Flexible'}
                          </span>
                        </div>
                      </div>
                      <Link to={`/network/job/${job.id}`} className="job-link">
                        View & Bid →
                      </Link>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {activeTab === 'bids' && (
            <div className="bids-section">
              {myBids.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📝</div>
                  <h3>No bids submitted yet</h3>
                  <p>Browse available jobs and submit your first bid.</p>
                  <button className="empty-cta" onClick={() => setActiveTab('browse')}>
                    Browse Jobs
                  </button>
                </div>
              ) : (
                <div className="bids-list">
                  {myBids.map((bid) => (
                    <div key={bid.id} className="bid-card influencer-bid">
                      <div className="bid-card-header">
                        <div className="bid-job-info">
                          <h3>{bid.jobTitle}</h3>
                          <span className="brand-name">by {bid.brandName}</span>
                        </div>
                        <span className={`bid-status status-${bid.status}`}>
                          {bid.status}
                        </span>
                      </div>
                      
                      <div className="bid-card-body">
                        <div className="bid-stat-row">
                          <div className="bid-stat-item">
                            <span className="stat-label">Your Bid</span>
                            <span className="stat-value highlight">${bid.amount?.toLocaleString()}</span>
                          </div>
                          <div className="bid-stat-item">
                            <span className="stat-label">Job Budget</span>
                            <span className="stat-value">${bid.jobBudget?.toLocaleString()}</span>
                          </div>
                          <div className="bid-stat-item">
                            <span className="stat-label">Delivery</span>
                            <span className="stat-value">{bid.deliveryDays || '—'} days</span>
                          </div>
                        </div>
                        
                        {bid.message && (
                          <div className="bid-proposal-preview">
                            <p>{bid.message.substring(0, 150)}{bid.message.length > 150 ? '...' : ''}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="bid-card-footer">
                        <span className="bid-date">
                          Submitted {new Date(bid.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        <div className="bid-card-actions">
                          <Link to={`/network/job/${bid.jobId}`} className="action-link">
                            View Job
                          </Link>
                          {bid.status === 'accepted' && (
                            <button 
                              className="action-btn-small message-btn"
                              onClick={() => openConversation(bid.conversationId)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                              Message
                            </button>
                          )}
                          {bid.status === 'accepted' && (
                            <button
                              className="action-btn-small submit-content-btn"
                              onClick={() => setSubmitTarget({ bid })}
                            >
                              Submit content
                            </button>
                          )}
                          {bid.status === 'pending' && (
                            <button 
                              className="action-btn-small"
                              onClick={() => setSelectedBidId(bid.id)}
                            >
                              Edit Bid
                            </button>
                          )}
                        </div>
                      </div>

                      {bid.status === 'accepted' && (
                        <div className="bid-submissions">
                          <div className="bid-submissions-head">
                            <h4>Your content submissions</h4>
                          </div>
                          <SubmissionsPanel
                            role="influencer"
                            query={{ bidId: bid.id }}
                            reloadKey={submissionsReload}
                            emptyText='Nothing submitted yet. Use "Submit content" to send your files, links and post caption for the brand to review.'
                            onResubmit={(previous) => setSubmitTarget({ bid, previous })}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <Messages
              userType="influencer"
              initialConversationId={messageConversationId}
              onActivity={() => fetchBadgeCounts(localStorage.getItem('token'))}
            />
          )}
        </div>
      </main>

      {/* Submit Content Modal */}
      {submitTarget && (
        <SubmitContentModal
          bid={submitTarget.bid}
          previous={submitTarget.previous}
          onClose={() => setSubmitTarget(null)}
          onSubmitted={() => setSubmissionsReload((n) => n + 1)}
        />
      )}

      {/* Bid Edit Modal */}
      {selectedBidId && (
        <BidEditModal
          bidId={selectedBidId}
          onClose={() => setSelectedBidId(null)}
          onUpdate={() => {
            const token = localStorage.getItem('token');
            fetchMyBids(token);
            fetchBadgeCounts(token);
          }}
        />
      )}
    </div>
  );
}

export default InfluencerDashboard;

